import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import ProfilePictureUpload from "@/components/shared/ProfilePictureUpload";
import LocationCombobox from "@/components/shared/LocationCombobox";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { PROFILE_TYPES, PROFILE_TYPE_LABEL, GENRES, INSTRUMENTS, GIG_TYPES, BAND_GROUP_TYPES, VENUE_TYPES } from "@/lib/constants";
import { Check } from "lucide-react";

const INTENTS = [
  { key: "find_work", label: "Find paid music work" },
  { key: "offer_services", label: "Offer music services" },
  { key: "hire_book", label: "Hire or book musicians" },
  { key: "post_gigs", label: "Post gigs / events" },
  { key: "find_venues", label: "Find venues" },
];

function Chip({ selected, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
        selected ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
      }`}>
      {selected && <Check className="w-3.5 h-3.5 inline mr-1" />}
      {children}
    </button>
  );
}

export default function Onboarding() {
  const { account, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [intents, setIntents] = useState([]);
  const [profileType, setProfileType] = useState("musician");
  const [form, setForm] = useState({
    display_name: account?.name || "", bio: "", profile_picture_url: "",
    venue_type: "", capacity: "", group_type: "Band", members_count: "",
    youtube_url: "", instagram_url: "", soundcloud_url: "", whatsapp_number: "",
  });
  const [location, setLocation] = useState(null);
  const [genres, setGenres] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [gigTypes, setGigTypes] = useState([]);

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const finish = async () => {
    if (!form.display_name.trim()) { toast.error("Display name required"); return; }
    if (!location) { toast.error("Location required"); return; }
    if (!form.profile_picture_url) { toast.error("Profile picture required"); return; }
    try {
      await api.post("/onboarding", { intents, roles: [profileType] });
      await api.post("/profiles", {
        profile_type: profileType, display_name: form.display_name.trim(),
        location_city: location.city, location_state: location.state,
        bio: form.bio, profile_picture_url: form.profile_picture_url,
        genres, instruments, gig_types: gigTypes,
        venue_type: profileType === "venue" ? form.venue_type : null,
        capacity: profileType === "venue" && form.capacity ? Number(form.capacity) : null,
        group_type: profileType === "band" ? form.group_type : null,
        members_count: profileType === "band" && form.members_count ? Number(form.members_count) : null,
        youtube_url: form.youtube_url || null, instagram_url: form.instagram_url || null,
        soundcloud_url: form.soundcloud_url || null,
        whatsapp_number: form.whatsapp_number || null,
      });
      toast.success("Profile created");
      await refresh();
      nav("/explore");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFCFD]">
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="flex items-center gap-2 mb-8">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`h-1.5 flex-1 rounded-full ${n <= step ? "bg-[#4F46E5]" : "bg-slate-200"}`} />
          ))}
        </div>

        {step === 1 && (
          <Card className="p-8 border-slate-200">
            <h1 className="font-display text-3xl font-bold tracking-tight">Welcome. Let's set you up.</h1>
            <p className="mt-2 text-slate-600">What do you want to do on Showfex?</p>
            <div className="flex flex-wrap gap-2 mt-6">
              {INTENTS.map((i) => (
                <Chip key={i.key} selected={intents.includes(i.key)}
                  onClick={() => toggle(intents, setIntents, i.key)}
                  testId={`intent-${i.key}`}>{i.label}</Chip>
              ))}
            </div>
            <div className="mt-8 flex justify-end">
              <Button onClick={() => setStep(2)} disabled={intents.length === 0}
                data-testid="onboarding-next-1"
                className="bg-[#0F172A] hover:bg-[#1E293B] text-white">Continue</Button>
            </div>
          </Card>
        )}

        {step === 2 && (
          <Card className="p-8 border-slate-200">
            <h1 className="font-display text-3xl font-bold tracking-tight">Set up your first profile</h1>
            <p className="mt-2 text-slate-600">You can add up to 2 profiles of each type later.</p>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              {PROFILE_TYPES.map((t) => (
                <button key={t} type="button" onClick={() => setProfileType(t)} data-testid={`ob-type-${t}`}
                  className={`p-4 rounded-xl border transition-colors ${
                    profileType === t ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-slate-200 hover:border-slate-400"
                  }`}>
                  <div className="font-semibold text-sm">{PROFILE_TYPE_LABEL[t]}</div>
                </button>
              ))}
            </div>
            <div className="mt-8 flex justify-between">
              <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={() => setStep(3)} data-testid="onboarding-next-2"
                className="bg-[#0F172A] hover:bg-[#1E293B] text-white">Continue</Button>
            </div>
          </Card>
        )}

        {step === 3 && (
          <Card className="p-8 border-slate-200">
            <h1 className="font-display text-3xl font-bold tracking-tight">{PROFILE_TYPE_LABEL[profileType]} details</h1>
            <p className="mt-2 text-slate-600">Only the essentials for now — you can polish anytime.</p>

            <div className="mt-6">
              <Label>Profile picture <span className="text-rose-600">*</span></Label>
              <div className="mt-2">
                <ProfilePictureUpload value={form.profile_picture_url} onChange={(v) => setForm({ ...form, profile_picture_url: v })} />
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Display name <span className="text-rose-600">*</span></Label>
                <Input data-testid="ob-name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
              </div>
              <div>
                <Label>Location <span className="text-rose-600">*</span></Label>
                <div className="mt-1"><LocationCombobox value={location} onChange={setLocation} testId="ob-location" /></div>
              </div>
              {profileType === "venue" && (
                <div>
                  <Label>Venue type</Label>
                  <select className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                    value={form.venue_type} onChange={(e) => setForm({ ...form, venue_type: e.target.value })}>
                    <option value="">Select</option>
                    {VENUE_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
              )}
              {profileType === "band" && (
                <div>
                  <Label>Group type</Label>
                  <select className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                    value={form.group_type} onChange={(e) => setForm({ ...form, group_type: e.target.value })}>
                    {BAND_GROUP_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
              )}
            </div>

            {(profileType === "musician" || profileType === "band" || profileType === "dj") && (
              <>
                <div className="mt-6">
                  <Label className="font-semibold">Genres</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {GENRES.map((g) => (<Chip key={g} selected={genres.includes(g)} onClick={() => toggle(genres, setGenres, g)}>{g}</Chip>))}
                  </div>
                </div>
                {(profileType === "musician" || profileType === "band") && (
                  <div className="mt-6">
                    <Label className="font-semibold">Instruments</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {INSTRUMENTS.map((g) => (<Chip key={g} selected={instruments.includes(g)} onClick={() => toggle(instruments, setInstruments, g)}>{g}</Chip>))}
                    </div>
                  </div>
                )}
                <div className="mt-6">
                  <Label className="font-semibold">Gig types</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {GIG_TYPES.map((g) => (<Chip key={g} selected={gigTypes.includes(g)} onClick={() => toggle(gigTypes, setGigTypes, g)}>{g}</Chip>))}
                  </div>
                </div>
              </>
            )}

            <div className="mt-6">
              <Label>Bio</Label>
              <Textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Tell potential clients about you." />
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input placeholder="YouTube URL (optional)" value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} />
              <Input placeholder="Instagram URL (optional)" value={form.instagram_url} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })} />
              <Input placeholder="SoundCloud URL (optional)" value={form.soundcloud_url} onChange={(e) => setForm({ ...form, soundcloud_url: e.target.value })} />
            </div>

            <div className="mt-6 border-t border-slate-100 pt-6">
              <Label className="font-semibold">Private WhatsApp (revealed only after acceptance)</Label>
              <Input className="mt-2" placeholder="WhatsApp with country code, e.g. 919812345678" value={form.whatsapp_number}
                onChange={(e) =>
  setForm({
    ...form,
    whatsapp_number: e.target.value.replace(/\D/g, ""),
  })
} data-testid="ob-whatsapp" />
            </div>

            <div className="mt-8 flex justify-between">
              <Button variant="ghost" onClick={() => setStep(2)}>Back</Button>
              <Button onClick={finish} data-testid="onboarding-finish"
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white">Create profile & continue</Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
